import { useEffect } from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import DynamicTitle from '../components/DynamicTitle';
import SEOStructuredData from '../components/SEOStructuredData';

const AIAutomation = () => {
  useEffect(() => {
    console.log("User visited AI Automation page");
  }, []);

  return (
    <div className="min-h-screen bg-black">
      <DynamicTitle 
        title="My Journey Into AI: From an 87-View Video to an AI-Driven Mission"
        description="Aram AI's complete journey into artificial intelligence: from discovering ChatGPT to becoming Armenia's leading AI content creator and consultant."
        keywords="AI automation, ChatGPT, voice AI, AI consulting, vibe coding, Armenia AI, Aram AI, AI personal brand, Make.com, n8n"
        canonicalUrl="https://aramb.am/products/ai-automation"
      />
      <SEOStructuredData 
        type="article"
        title="My Journey Into AI: From an 87-View Video to an AI-Driven Mission"
        description="The complete story of how Aram discovered AI, pivoted from e-commerce, and built a thriving AI automation business in Armenia."
        url="https://aramb.am/products/ai-automation"
        author="Aram AI"
        datePublished="2024-11-14"
      />
      <Header />
      
      <main className="container mx-auto px-4 py-12 max-w-4xl">
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 font-heading glow-text">
            My Journey Into AI: From an 87-View Video to an AI-Driven Mission
          </h1>
        </div>

        {/* Article Content */}
        <div className="max-w-4xl mx-auto mb-24">
          <article className="glass-panel rounded-2xl overflow-hidden">
            <div className="p-12 space-y-8">
              <div className="prose prose-invert prose-xl max-w-none leading-relaxed">
                <p className="text-xl leading-[1.8] text-gray-200 font-light mb-8 first-letter:text-6xl first-letter:font-bold first-letter:text-purple-300 first-letter:float-left first-letter:mr-3 first-letter:mt-1">
                  It all started on a random evening in November 2022. I was at a friend's house outside the city, and as I was about to have dinner, I pulled up YouTube. A video caught my eye. It was about a "new tool that is going to change the world." The video had 87 views and was posted 7 hours earlier by a random Indian guy with fewer than 100 subscribers. My intuition told me to click.
                </p>

                <p className="text-[17px] leading-[1.75] text-gray-200 font-article font-normal mb-6">
                  After watching a few minutes about this "ChatGPT" thing, I clicked the link in the description to try it myself. I will never forget that feeling—seeing the words appear on the screen, feeling like the computer was <em>talking</em> to me.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  I was obsessed. For the next few weeks, I told everyone I spoke to about it. It was crystal clear that this was something I needed to dive deep into.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">Learning in the Background</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  Soon, the AI hype went mainstream. Influencers started making content and exploring what could be done. At the time, I was fully focused on building my Amazon FBA business with an investor. We were in the first steps of designing the brand, the product, and more. So, while my focus wasn't 100% on "how do I make money with this," I was 100% focused on following the news.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  My feed became all about AI. I learned the basic concepts, the history, the different types of AI, shiny new tools, RAG, and everything in between. I also started to implement ChatGPT and Midjourney (the most popular tools back then) into our e-commerce workflows for generating reviews, making listing images, and more.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  I was learning a lot, but I wasn't monetizing any of it yet.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">Closing the Amazon Chapter and Taking the Leap</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  As I wrote on my Amazon FBA page, I eventually reached a point of burnout with e-commerce. I closed my agency and parted ways with my partner to fully dedicate myself to exploring and building in the world of AI.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  It was time to decide where to focus. It was already clear you couldn't keep up with all the news, let alone all the <em>things</em> you could do. I had to choose a niche. I saw people going into content creation, consultation, and more.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  I chose <strong>Voice AI</strong>. It was SO FRESH. It was the very first steps of the voice AI world.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">My First Attempt: The Voice AI Agency</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  I learned about the new tools, "vibe coded" a landing page for my services, opened a company, and learned how to use platforms like Vapi and Make.com to build voice AI agents that could handle real tasks.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  After just a few weeks of learning, I wasn't 100% ready to fulfill all the work, but everyone I talked to was interested. I started onboarding clients and managed to land four successful projects in the first month.
                </p>

                <div className="bg-purple-900/20 border-l-4 border-purple-500 p-6 my-8 rounded-r-lg">
                  <p className="text-lg leading-[1.8] text-gray-200 italic">
                    But I quickly hit a wall. I realized the real bottleneck of my voice AI agency was me. I was a solopreneur handling <em>everything</em> – sales, marketing, and fulfillment.
                  </p>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  It was hard to keep up with the changes happening in voice AI every single day. I felt exhausted and realized I couldn't scale without a co-founder. After four months of searching for a good fit and coming up empty, I made the tough decision to quit voice AI and pivot.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">Finding My True Path: Consulting, Vibe Coding, and Armenia</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  At that exact time, a friend managing reArmenia, a well-known non-profit in Armenia, asked if I could be an AI consultant for their academy members. I said yes. I started helping people in their fundraising programs choose the right AI tools and workflows.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  Meanwhile, I was still exploring. Lovable.dev was founded, and I became addicted to it. I built <a href="/products/prompt-manager" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline decoration-purple-500/50 hover:decoration-purple-400 transition-all">a full SaaS</a> in three weeks. I built <a href="/products/vasilenko-ai" target="_blank" rel="noopener noreferrer" className="text-purple-400 hover:text-purple-300 underline decoration-purple-500/50 hover:decoration-purple-400 transition-all">a fun memecoin</a>. This was the moment I truly realized: <strong>I can build basically whatever I think of.</strong>
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  I knew solopreneurship was the path for me, and I decided to focus on things I could do on my own. This is when I got the idea to build a personal brand and pivot to my own market: <strong>Armenia</strong>.
                </p>

                <div className="bg-green-900/20 border-l-4 border-green-500 p-6 my-8 rounded-r-lg">
                  <p className="text-lg leading-[1.8] text-gray-200">
                    It was an empty space. My people really needed this. Most creators in Armenia either don't make this kind of content or they focus on foreign markets because "Armenia is too small." I disagreed.
                  </p>
                </div>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  After a few months of consulting for reArmenia, doing webinars, and building a reputation, I knew it was time to go bigger.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  I quit everything else and went all-in on my own. I started building a personal brand, going fully horizontal:
                </p>

                <ul className="list-disc list-inside space-y-3 mb-6 text-lg text-gray-300 ml-4">
                  <li><strong>Instagram:</strong> 6 reels per week</li>
                  <li><strong>TikTok & YouTube Shorts:</strong> Repurposing the same content</li>
                  <li><strong>LinkedIn:</strong> Posting my insights</li>
                  <li><strong>YouTube:</strong> Longer-form videos on AI news</li>
                  <li><strong>Telegram:</strong> I had already automated an AI news channel in Armenian</li>
                  <li><strong>Community:</strong> I started my own free chat for AI enthusiasts</li>
                </ul>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  I was tired of repeating the same things to different people, and launching a personal brand was the best decision I could have made.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">From Personal Brand to Real-World Impact</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  This journey—the content, the networking, the consulting—led me to great clients who became friends. I started working with <strong>Digitain</strong>, "vibe coding" web apps and building systems for them. I began working with <strong>TCF</strong>, maybe the most innovative marketing agency in the country. Then came <strong>Gadgetflow</strong>, and other opportunities with businesses in different industries, from betting companies to gyms.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  My focus had officially switched from just "Voice AI" to a broader, more impactful role:
                </p>

                <ul className="list-disc list-inside space-y-3 mb-6 text-lg text-gray-300 ml-4">
                  <li>Business & AI Consulting</li>
                  <li>Vibe Coding (building apps and tools)</li>
                  <li>AI Automations (using n8n and Make.com)</li>
                </ul>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  After building my social presence, I launched my "Vibe Coding" course and a paid Telegram community, «Վայբանոց», connecting talented people.
                </p>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  And hey, it's only been 6 months since I started the personal brand.
                </p>

                <h2 className="text-3xl font-bold mb-6 text-white">Where I Am Today (November 2025)</h2>

                <p className="text-lg leading-[1.8] text-gray-300 mb-6 font-article">
                  Right now, I've saved countless hours for my clients through AI automation and by creating custom AI agents. I have great companies as my clients, a solid social media presence with <strong>11.5k Instagram followers</strong> and <strong>700k+ monthly impressions</strong>, and most importantly—I'm bringing a lot of value to the people, empowering them with AI.
                </p>

                <div className="bg-gradient-to-r from-purple-900/30 to-blue-900/30 border border-purple-500/30 p-8 my-12 rounded-xl text-center">
                  <p className="text-2xl font-semibold text-white mb-4">
                    This is just the beginning.
                  </p>
                  <p className="text-lg text-gray-200">
                    The AI revolution in Armenia has only just started, and I'm here to lead it.
                  </p>
                </div>
              </div>
            </div>
          </article>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default AIAutomation;
